import React, { useState } from "react";
import AppointmentTypesManager from "./AppointmentTypesManager";
import BusinessHours from "./BusinessHours";
import styled from "styled-components";
import {
  IconClock,
  IconCalendarEvent,
  IconBuildingStore,
} from "@tabler/icons-react";

// Styled Components
const Container = styled.div`
  margin: 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    background: #ffffff;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  margin-top: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    margin-top: 0px;
    gap: 16px;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: #212529;
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 21px;
    justify-content: center;
  }
`;

const TitleIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const Subtitle = styled.p`
  color: #6c757d;
  font-size: 16px;
  margin: 8px 0 0 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 14px;
    margin: 4px 0 0 0;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: white;
  border-radius: 16px;
  padding: 6px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 123, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 6px;
    border-radius: 12px;
    padding: 8px;
  }
`;

const TabButton = styled.button`
  flex: 1;
  background: ${(props) =>
    props.active ? "linear-gradient(135deg, #007bff, #0056b3)" : "transparent"};
  color: ${(props) => (props.active ? "white" : "#6c757d")};
  border: none;
  border-radius: 12px;
  padding: 16px 20px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    background: ${(props) =>
      props.active
        ? "linear-gradient(135deg, #0056b3, #004085)"
        : "rgba(0, 123, 255, 0.05)"};
    color: ${(props) => (props.active ? "white" : "#007bff")};
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.active
        ? "0 8px 25px rgba(0, 123, 255, 0.3)"
        : "0 4px 15px rgba(0, 123, 255, 0.1)"};
  }

  &:hover:before {
    left: 100%;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 18px 20px;
    font-size: 16px;

    &:hover {
      transform: none;
    }
  }
`;

const ContentContainer = styled.div`
  background: transparent;
  border-radius: 16px;
  min-height: 400px;
  position: relative;

  @media (max-width: 768px) {
    border-radius: 12px;
    min-height: 300px;
  }
`;

const TabContent = styled.div`
  opacity: ${(props) => (props.active ? 1 : 0)};
  visibility: ${(props) => (props.active ? "visible" : "hidden")};
  position: ${(props) => (props.active ? "static" : "absolute")};
  top: 0;
  left: 0;
  right: 0;
  transition: all 0.3s ease;
  transform: ${(props) =>
    props.active ? "translateY(0)" : "translateY(20px)"};

  @media (max-width: 768px) {
    transform: ${(props) =>
      props.active ? "translateY(0)" : "translateY(10px)"};
  }
`;

const FeaturesBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);

  @media (max-width: 768px) {
    position: static;
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    font-size: 10px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const StatCard = styled.div`
  background: ${(props) => (props.active ? "#e3f2fd" : "white")};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid
    ${(props) => (props.active ? "#2196f3" : "rgba(0, 0, 0, 0.05)")};
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${(props) =>
      props.active ? "#1976d2" : "rgba(0, 123, 255, 0.2)"};
  }

  @media (max-width: 768px) {
    padding: 12px;

    &:hover {
      transform: none;
    }
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  background: ${(props) =>
    props.color || "linear-gradient(135deg, #007bff, #0056b3)"};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    margin-bottom: 8px;
  }
`;

const StatLabel = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const StatDescription = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
`;

export default function BusinessSettings() {
  const [activeTab, setActiveTab] = useState("openingHours");

  const tabs = [
    {
      key: "openingHours",
      label: "Horários de Funcionamento",
      icon: IconClock,
      component: BusinessHours,
      description: "Configure os dias e horários de atendimento",
      color: "linear-gradient(135deg, #007bff, #0056b3)",
    },
    {
      key: "appointmentTypes",
      label: "Tipos de Atendimento",
      icon: IconCalendarEvent,
      component: AppointmentTypesManager,
      description: "Gerencie os tipos de serviços oferecidos",
      color: "linear-gradient(135deg, #28a745, #20c997)",
      badge: "Pro",
    },
  ];

  const currentTab = tabs.find((tab) => tab.key === activeTab);

  return (
    <Container className="">
      <Header>
        <div>
          <Title>
            <TitleIcon>
              <IconBuildingStore size={24} />
            </TitleIcon>
            Configurações do Negócio
          </Title>
        </div>
      </Header>

      <StatsContainer>
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <StatCard
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              <StatIcon color={tab.color}>
                <IconComponent size={24} />
              </StatIcon>
              <StatLabel>{tab.label}</StatLabel>
              <StatDescription>{tab.description}</StatDescription>
            </StatCard>
          );
        })}
      </StatsContainer>

      <ContentContainer>
        {tabs.map((tab) => {
          const ComponentToRender = tab.component;
          return (
            <TabContent key={tab.key} active={activeTab === tab.key}>
              <ComponentToRender />
            </TabContent>
          );
        })}
      </ContentContainer>
    </Container>
  );
}
